export type User = {
  id: number;
  email: string;
  name: string;
  role: 'superadmin' | 'exec' | 'client';
  client_id: number | null;
};

export type AgencyClient = {
  client_id: number;
  name: string;
  slug: string;
  industry: string | null;
  franchise_brand: string | null;
  territories: number;
  spend: {
    total: number;
    prior_period: number | null;
    change: number | null;
    change_pct: number | null;
    by_platform: Array<{ platform: string; spend: number; clicks: number | null; conversions: number | null }>;
  };
  health: null | {
    overall: number;
    retention_risk: 'low' | 'medium' | 'high';
    scored_on: string;
    components: Record<string, number>;
  };
  latest_report: null | {
    report_id: number;
    period: string;
    period_end?: string | null;
    calls: number;
    booked: number;
    booking_rate: number;
    revenue: number | null;
    roas: number | null;
  };
  latest_audit: null | { audit_id: number; date: string; score: number };
  connections: {
    google_ads: number;
    ghl: boolean;
    has_spend: boolean;
    has_call_data: boolean;
    has_website_audit: boolean;
  };
};

export type AgencyOverview = {
  period_days: number;
  /** The days the spend covers, both included (routes/agency.js). */
  period?: { from: string; to: string };
  latest_spend_date: string | null;
  client_count: number;
  totals: {
    spend: number;
    clients_with_spend: number;
    clients_without_spend: number;
    avg_health_score: number | null;
    at_risk: number;
  };
  clients: AgencyClient[];
  note: string;
};

export type Client = {
  id: number;
  name: string;
  slug: string;
  industry: string | null;
  client_type: string;
  service_tier: string;
  /** Services switched off for this client (services.ts). */
  services_off?: string[];
  status: string;
  monthly_ad_spend: number | null;
  primary_contact: string | null;
  primary_email: string | null;
  location_count: number;
};

/** What each task status is called on screen (the Work queue's own words). */
export const TASK_STATUS_LABEL: Record<string, string> = { pending: "To do", in_progress: "In progress", completed: "Done", snoozed: "Snoozed", escalated: "Escalated" };

export type Task = {
  id: number;
  title: string;
  body: string | null;
  client_id: number | null;
  client_name: string | null;
  status: string;
  priority: string;
  task_type: string;
  due_date: string | null;
  assigned_to_name: string | null;
};

export type HealthScore = {
  id: number;
  client_id: number;
  client_name: string;
  client_slug: string;
  overall_score: number;
  performance_score: number;
  communication_score: number;
  signal_score: number;
  execution_score: number;
  retention_risk: 'low' | 'medium' | 'high';
  score_date: string;
  score_notes?: string;
  /** The client's own status; only active clients are rescored nightly. */
  client_status?: string | null;
};
